// Customer-facing rendering of activity_logs docs - the website's ONE
// registry, per app_design_docs/ACTIVITY_LOG_CONTRACT.md sections 2-4.
// Built against the POST-migration registry: release round R1 runs the data
// migration before this renderer deploys, so retired/renamed types
// (coin_redemption, redem_stamps_for_prize, Login) no longer exist in
// production data and fall through to "unknown -> hidden" here.

// Contract section 2: types with a dedicated customer-facing label.
const TYPE_LABELS = {
  new_stamps_with_visit: '🧸 New Stamp',
  stamps_redeemed: '🏆 Prize Claimed',
  referral_bonus_new_customer: '🎁 Welcome Bonus',
  referral_bonus_referrer: '🎉 Referral Reward',
  gift_card_activated: '🎁 Gift Card Activated',
  gift_card_redeemed: '🎁 Gift Card Redeemed',
  gift_card_transferred_out: '🎁 Gift Card Transferred',
  gift_card_received: '🎁 Gift Card Received',
};

// Contract section 3 rule 3: rendered purely from the value adjustment;
// only increases get an annotation, decreases stay bare.
const VALUE_BASED_TYPES = new Set([
  'admin_update',
  'account_transfer_out',
  'account_transfer_in',
  'withdraw_refund',
  'stamps_decreased_order_canceled',
]);

// Row order matches the shipped Activity History layout.
const BALANCE_KEYS = ['points', 'coins', 'stamps'];

// Customer vocabulary: the coins field renders as "Tokens" (matches the
// iOS IOS-Q18 relabel; Lily 2026-08-31)
const ROW_TITLES = { points: 'Points', coins: 'Tokens', stamps: 'Stamps' };

/**
 * Lenient balance-change extraction (contract section 4): value maps are
 * PARTIAL - a key absent from either side means "this balance unaffected"
 * and gets no row; only numeric, actually-changed pairs render.
 * @returns {Array<{key: string, title: string, previous: number, next: number}>}
 */
export const balanceChanges = (log) => {
  const changes = [];
  for (const key of BALANCE_KEYS) {
    const previous = log?.previousValues?.[key];
    const next = log?.newValues?.[key];
    if (typeof previous === 'number' && typeof next === 'number' && previous !== next) {
      changes.push({ key, title: ROW_TITLES[key], previous, next });
    }
  }
  return changes;
};

/**
 * Decide how one activity_logs doc renders to the member.
 * @returns {{label: string|null, changes: Array} | null} null = do not render.
 */
export const presentActivityLog = (log) => {
  if (!log || log.hidden === true) return null;

  const type = log.type;
  if (typeof type !== 'string') return null;

  // gift_card_* are EVENT logs: bare label row, no amounts - the giftCard map
  // is read only by the super-admin audit view (decided 2026-08-28), and a
  // redeemed card's stamp increase arrives as its own new_stamps_with_visit log.
  if (type.startsWith('gift_card_') && TYPE_LABELS[type]) {
    return { label: TYPE_LABELS[type], changes: [] };
  }

  const changes = balanceChanges(log);

  if (TYPE_LABELS[type]) {
    // Section 3 rule 4: zero net change renders nothing.
    if (changes.length === 0) return null;
    return { label: TYPE_LABELS[type], changes };
  }

  if (VALUE_BASED_TYPES.has(type)) {
    if (changes.length === 0) return null;
    const labels = [];
    const coins = changes.find((c) => c.key === 'coins');
    const points = changes.find((c) => c.key === 'points');
    if (coins && coins.next > coins.previous) labels.push('🪙 Token Saved');
    if (points && points.next > points.previous) labels.push('🎟️ Points Saved');
    return { label: labels.length > 0 ? labels.join(' · ') : null, changes };
  }

  // HIDDEN types (order_marked_completed, note_taking, login) and anything
  // unknown: never shown to customers, never as a raw type string.
  return null;
};
